import { Component } from '@theme/component';
import {
  debounce,
  isClickedOutside,
  lockScroll,
  onAnimationEnd,
  unlockScroll,
} from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];
  connectedCallback() {
    super.connectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    unlockScroll(this.refs.dialog);
  }
  #handleResize = debounce(() => {
    const { minWidth: minWidth, maxWidth: maxWidth } = this;
    if (!minWidth && !maxWidth) return;
    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);
  #previousScrollY = 0;
  showDialog() {
    const { dialog: dialog } = this.refs;
    if (dialog.open) return;
    this.#previousScrollY = getScrollTop();
    requestAnimationFrame(() => {
      lockScroll(dialog);
      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }
  closeDialog = async () => {
    const { dialog: dialog } = this.refs;
    if (!dialog.open) return;
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';
    await onAnimationEnd(dialog, undefined, { subtree: false });
    unlockScroll(dialog);
    scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    dialog.close();
    dialog.classList.remove('dialog-closing');
    this.dispatchEvent(new DialogCloseEvent());
  };
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };
  #handleClick(event) {
    const { dialog: dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeDialog();
  }
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}
if (!customElements.get('dialog-component'))
  customElements.define('dialog-component', DialogComponent);
class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }
  static eventName = 'dialog:open';
}
export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }
  static eventName = 'dialog:close';
}
document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open: open } = event.target;
        if (open) {
          lockScroll(event.target);
        } else {
          unlockScroll(event.target);
        }
      }
    }
  },
  { capture: true },
);
